import type { FormDefinition, FormField } from './form-definition.js';

export const FORM_FIELD_TYPES = [
  'text',
  'phone',
  'singleChoice',
  'multilineText',
  'number',
  'date',
  'time',
  'multiChoice',
  'cascadingSelect',
  'checklist',
  'image',
  'file',
  'signature',
  'location',
  'address',
  'sectionTitle',
] as const;

export type FormFieldType = typeof FORM_FIELD_TYPES[number];
export type FormFieldAnswerKind =
  | 'none'
  | 'scalar'
  | 'choice'
  | 'choice-list'
  | 'path'
  | 'checklist'
  | 'image-list'
  | 'file-list'
  | 'signature'
  | 'location'
  | 'address';

export const FORM_FIELD_CAPABILITY_KEYS = Object.freeze({
  base: 'forms.base_fields_r2',
  structured: 'forms.structured_fields_r3',
  checklist: 'forms.checklist_core_r4',
  submissionMedia: 'forms.submission_media_r5',
  checklistMedia: 'forms.checklist_media_evidence_r6',
  image: 'forms.image_core_r7',
  file: 'forms.file_collection_r8',
  signature: 'forms.signature_core_r9',
  locationAddress: 'forms.location_address_r10',
} as const);

export interface FormFieldRegistration<Type extends FormFieldType = FormFieldType> {
  readonly type: Type;
  readonly label: Readonly<{ en: string; zh: string }>;
  readonly group: 'basic' | 'structured' | 'evidence' | 'layout';
  readonly answerKind: FormFieldAnswerKind;
  readonly creationCapabilityKeys: readonly string[];
  readonly adapters: Readonly<{
    editor: Type;
    publicRenderer: Type;
    validator: Type;
    historyRenderer: Type;
    exportFormatter: Type;
  }>;
}

function registration<Type extends FormFieldType>(
  type: Type,
  label: { en: string; zh: string },
  group: FormFieldRegistration['group'],
  answerKind: FormFieldAnswerKind,
  creationCapabilityKeys: readonly string[] = [],
): FormFieldRegistration<Type> {
  return Object.freeze({
    type,
    label: Object.freeze(label),
    group,
    answerKind,
    creationCapabilityKeys: Object.freeze([...creationCapabilityKeys]),
    adapters: Object.freeze({
      editor: type,
      publicRenderer: type,
      validator: type,
      historyRenderer: type,
      exportFormatter: type,
    }),
  });
}

const base = FORM_FIELD_CAPABILITY_KEYS.base;
const media = FORM_FIELD_CAPABILITY_KEYS.submissionMedia;

export const FORM_FIELD_REGISTRY = Object.freeze({
  text: registration('text', { en: 'Short text', zh: '单行文本' }, 'basic', 'scalar'),
  phone: registration('phone', { en: 'Phone', zh: '电话' }, 'basic', 'scalar'),
  singleChoice: registration('singleChoice', { en: 'Single choice', zh: '单选' }, 'basic', 'choice'),
  multilineText: registration('multilineText', { en: 'Long text', zh: '多行文本' }, 'basic', 'scalar', [base]),
  number: registration('number', { en: 'Number', zh: '数字' }, 'basic', 'scalar', [base]),
  date: registration('date', { en: 'Date', zh: '日期' }, 'basic', 'scalar', [base]),
  time: registration('time', { en: 'Time', zh: '时间' }, 'basic', 'scalar', [base]),
  multiChoice: registration('multiChoice', { en: 'Multiple choice', zh: '多选' }, 'basic', 'choice-list', [base]),
  cascadingSelect: registration('cascadingSelect', { en: 'Cascading select', zh: '级联选择' }, 'structured', 'path', [FORM_FIELD_CAPABILITY_KEYS.structured]),
  checklist: registration('checklist', { en: 'Checklist', zh: '检查清单' }, 'structured', 'checklist', [FORM_FIELD_CAPABILITY_KEYS.checklist]),
  image: registration('image', { en: 'Images', zh: '图片' }, 'evidence', 'image-list', [media, FORM_FIELD_CAPABILITY_KEYS.image]),
  file: registration('file', { en: 'Files', zh: '文件' }, 'evidence', 'file-list', [media, FORM_FIELD_CAPABILITY_KEYS.file]),
  signature: registration('signature', { en: 'Signature', zh: '签名' }, 'evidence', 'signature', [media, FORM_FIELD_CAPABILITY_KEYS.signature]),
  location: registration('location', { en: 'Location', zh: '定位' }, 'evidence', 'location', [FORM_FIELD_CAPABILITY_KEYS.locationAddress]),
  address: registration('address', { en: 'Address', zh: '地址' }, 'evidence', 'address', [FORM_FIELD_CAPABILITY_KEYS.locationAddress]),
  sectionTitle: registration('sectionTitle', { en: 'Section title', zh: '分节标题' }, 'layout', 'none', [base]),
} satisfies { [Type in FormFieldType]: FormFieldRegistration<Type> });

export function formFieldRegistration<Type extends FormFieldType>(type: Type): FormFieldRegistration<Type> {
  return FORM_FIELD_REGISTRY[type] as FormFieldRegistration<Type>;
}

export function canCreateFormField(type: FormFieldType, capabilities: Iterable<string>): boolean {
  const available = capabilities instanceof Set ? capabilities : new Set(capabilities);
  return formFieldRegistration(type).creationCapabilityKeys.every((key) => available.has(key));
}

export function formDefinitionRequiredCapabilityKeys(definition: FormDefinition): string[] {
  const keys = new Set<string>();
  for (const field of definition.fields) {
    for (const key of formFieldRegistration(field.type).creationCapabilityKeys) keys.add(key);
    if (Object.hasOwn(field, 'unique')) keys.add(FORM_FIELD_CAPABILITY_KEYS.base);
    if (field.type === 'checklist' && (
      field.allowResultImages
      || field.items.some((item) => (item.instructionImages?.length ?? 0) > 0)
    )) {
      keys.add(FORM_FIELD_CAPABILITY_KEYS.submissionMedia);
      keys.add(FORM_FIELD_CAPABILITY_KEYS.checklistMedia);
    }
  }
  return [...keys];
}

export function assertRegisteredFormField(field: FormField): FormFieldRegistration {
  return formFieldRegistration(field.type);
}
